/**
 * AUTH-WAY-1 (FO3) — the session-ended notice, carried across the redirect.
 *
 * THE PROBLEM THIS SOLVES. When a session goes stale the app clears the token, drops the user to
 * null, and the route guard sends the reader to /login. The only thing that said WHY was a toast
 * fired from `useAuth`'s auth-stale handler — and a toast is the wrong carrier for this:
 *   - it is dismissed by the navigation that follows it, so the reader can arrive at a login form
 *     with no explanation at all: a silent redirect in everything but name;
 *   - it is transient, so a reader who looked away has no way to recover the reason.
 *
 * So the reason is PERSISTED for exactly one read. `sessionStorage` rather than a module variable
 * because the redirect can remount the tree, and rather than `localStorage` because the notice must
 * not outlive the tab — a stale "your session expired" on tomorrow's first visit would be a lie.
 *
 * The stored payload carries the reason code as well as the message, so the login page can tell
 * apart the two expiry regimes (idle vs absolute max lifetime) once the backend distinguishes them.
 * It does not today — both raise a plain-string 401 with no reason code — which is precisely why
 * `apiErrors` supplies the copy and this module stores whatever it resolved.
 *
 * Every accessor is wrapped: sessionStorage throws in a private window with site data blocked, and
 * a notice is never important enough to break a login page over.
 */
const NOTICE_KEY = "cognivio_session_notice";

/** Record why the session ended. Called from the auth-stale handler, before the redirect. */
export function setSessionEndedNotice(detail) {
  if (typeof window === "undefined") return;
  const message = typeof detail?.message === "string" ? detail.message : "";
  if (!message) return;
  try {
    window.sessionStorage.setItem(
      NOTICE_KEY,
      JSON.stringify({
        message,
        reason_code: detail?.reason_code || null,
        status: detail?.status ?? null,
      })
    );
  } catch {
    // no-op: see the header. A notice is never worth an exception.
  }
}

/**
 * Read the notice and clear it in the same breath, so it shows exactly once. A reader who signs in
 * again and later signs out on purpose must not meet a stale expiry banner.
 */
export function consumeSessionEndedNotice() {
  if (typeof window === "undefined") return null;
  let raw = null;
  try {
    raw = window.sessionStorage.getItem(NOTICE_KEY);
    window.sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed.message === "string" && parsed.message ? parsed : null;
  } catch {
    return null;
  }
}

/** Drop any pending notice without rendering it (a deliberate sign-out is not an expiry). */
export function clearSessionEndedNotice() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // no-op
  }
}
